/**
 * Ночь переключения: пять шагов, каждый со своей кнопкой.
 *
 * Отдельным экраном, а не кнопками, разбросанными по консоли, — потому что в
 * час ночи важно не «где эта команда», а «где мы сейчас». Порядок шагов здесь
 * и есть содержание экрана.
 *
 * Ни один шаг не начинается сам. Консоль не ведёт переключение по расписанию и
 * не переходит к следующему шагу по успеху предыдущего: между шагами человек
 * смотрит на результат и решает — идти дальше или откатываться. Автоматизация
 * этого решения означала бы переезд, который нельзя остановить.
 *
 * Опасные шаги спросят код второго фактора: это делает общий слой вызова, а не
 * этот экран. Остановка боевого магазина не должна совершаться тем, у кого
 * просто оказался ключ.
 */
import { useEffect, useState } from 'react';
import { useQuery } from '../transport/hooks.ts';
import { request } from '../transport/socket.ts';
import { bytes, count } from '../lib/format.ts';
import { agoWords } from '../lib/rate.ts';
import { Confirm, useConfirm } from '../components/Confirm.tsx';
import { CampaignLog } from '../components/CampaignLog.tsx';
import { CampaignFinish } from '../components/CampaignFinish.tsx';
import type { AgentsSnapshot, RestoreView } from '../../shared/protocol/agents.ts';

/** Что переезжает. Единственное место, где это названо. */
const SITE = 'eurobrands-shop.de';
/*
 * Опознаватель мероприятия — латиницей.
 *
 * Он попадает в адрес, а кириллица в адресе превращается в
 * `camp-%D0%BF%D0%B5%D1%80%D0%B5...` — сорок знаков вместо четырнадцати.
 * Такую ссылку нельзя ни прочитать глазом, ни продиктовать, а посылают их
 * как раз тогда, когда некогда разбираться.
 */
const CAMPAIGN = 'camp-shop-move';

interface State {
  siteId: string;
  maintenance: { on: boolean; ok: boolean; at: string; msg: string } | null;
  publicCheck: { ok: boolean; at: string; msg: string; addresses: string[] } | null;
  publicBeat: { at: string; msg: string; done: boolean } | null;
  /** Что стоит в зоне прямо сейчас — по ответу провайдера, а не по догадке. */
  zone: { records: { name: string; values: string[] }[]; readAt: string; error: string | null };
  newSite: {
    hostId: string | null; declared: boolean; adopted: boolean; enabled: boolean;
    sources: string[];
  } | null;
  newRun: {
    runId: string; state: string; phase: string | null;
    startedAt: string; finishedAt: string | null;
    files: number; bytes: number; uploaded: number;
  } | null;
  lastRun: {
    runId: string; state: string; startedAt: string; finishedAt: string | null;
    files: number; bytes: number; reason: string;
  } | null;
  lastRestore: RestoreView | null;
  /** Все параметры мероприятия, решённые заранее. Выбирать нечего. */
  recipe: {
    snapshotId: string | null; targetHostId: string | null; into: string;
    sources: string[]; skipTables: string[]; restart: boolean; importDumps: boolean;
    keep: string[]; why: string;
  } | null;
}

/**
 * Отметка шага: сделан, идёт или ещё впереди.
 *
 * Владелец: «я уже восстанавливаю данные, а вкладка ничего не показывает —
 * получается, выполнены все предыдущие, а не видно». Шаг без состояния — это
 * пункт списка дел; шаг с состоянием — место, где мы находимся.
 */
function Step({ n, title, mark, children }: {
  n: number; title: string;
  mark: { state: 'done' | 'now' | 'wait' | 'bad'; word: string };
  children: React.ReactNode;
}): React.JSX.Element {
  const glyph = { done: '✓', now: '●', wait: '', bad: '!' }[mark.state];
  return (
    <section className={`wizard__step step step--${mark.state}`}>
      <h3 className="wizard__title">
        <span className="wizard__num">{n}</span> {title}
        <span className={`step__mark step__mark--${mark.state}`}>
          {glyph} {mark.word}
        </span>
      </h3>
      {children}
    </section>
  );
}

interface Bill {
  currency: string;
  lines: { name: string; what: string; monthly: number; stopsWithServer: boolean }[];
  monthly: number; monthlyWithServer: number; monthlyLeftover: number;
}

interface Cost {
  bill: Bill;
  /** Только те машины, которые гасим: это и есть экономия. */
  going: Bill;
  goingNames: string[];
  /** Машины, которых в кабинете уже нет: это состоявшаяся экономия. */
  gone: { name: string; type: string; lastSeen: string }[];
  source: string;
  why: string | null;
  when: { partialInvoiceFor: string; firstCleanInvoiceFor: string; hoursCharged: number };
}

interface Zone {
  zones: { id: string; name: string; accountId: string; ttl: number | null }[];
}

export function Switchover(): React.JSX.Element {
  const { data: st, reload } = useQuery<State>('switchover.state', { siteId: SITE });
  /*
   * Экран сам себя обновляет, пока на него смотрят.
   *
   * Все живые вести этой ночи — отчёты машин: они приходят через секунды после
   * нажатия и НЕ через то соединение, в котором нажали. Экран, обновляющийся
   * только по нажатию, показывает состояние на момент нажатия — то есть «ещё
   * ничего не случилось» ровно тогда, когда всё и случается.
   *
   * Три секунды: шаги ночи меряются минутами, а терпение в час ночи —
   * секундами.
   */
  useEffect(() => {
    const t = setInterval(() => reload(), 3000);
    return () => clearInterval(t);
  }, []);
  const { data: agents } = useQuery<AgentsSnapshot>('agents.list');
  const { data: runs } = useQuery<{ items: RestoreView[] }>('restore.runs');
  const { data: zones } = useQuery<Zone>('dns.zones');
  const { data: cost } = useQuery<Cost>('cloud.cost');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [said, setSaid] = useState<string | null>(null);
  const confirm = useConfirm();

  const machines = agents?.agents ?? [];
  const source = machines.find((a) => a.label === 'prod.eurobrands-shop.de');
  const target = machines.find((a) => a.label === 'web.eurobrands-shop.de');
  const zone = zones?.zones.find((z) => z.name === SITE);
  const lastRestore = (runs?.items ?? [])[0] ?? null;

  /** Адрес приёмника берём из журнала подключений его агента, а не из письма. */
  const targetAddress = '95.217.116.80';
  const sourceAddress = '138.199.195.202';

  async function run(what: string, method: string, params: unknown): Promise<void> {
    setBusy(true); setFailure(null); setSaid(null);
    try {
      await request(method as Parameters<typeof request>[0], params);
      setSaid(`${what}: команда отправлена, ждём отчёта машины`);
      reload();
    } catch (err) {
      setFailure((err as { msg?: string }).msg ?? 'не получилось');
    } finally {
      setBusy(false);
    }
  }

  const closed = st?.maintenance?.on === true && st.maintenance.ok;

  /*
   * Состояние каждого шага — по делу, а не по нашей памяти о нажатиях.
   *
   * Экран переживает перезагрузку, смену браузера и пересменку человека: в
   * четыре утра на него может смотреть не тот, кто нажимал в час. Значит
   * читать надо следы работы, а не то, что мы про неё запомнили.
   */
  const pass = st?.lastRun ?? null;
  const rest = st?.lastRestore ?? null;
  const restoreLive = rest?.state === 'running' || rest?.state === 'granted';
  const passLive = pass?.state === 'running';

  /*
   * Легла ли правка. `null` — кабинет не ответил, и это не «прежний адрес», а
   * «мы не знаем»: разница между ними — разница между ожиданием и действием.
   */
  const ready = st?.newSite ?? null;
  const first = st?.newRun ?? null;
  const zoneRows = st?.zone.records ?? [];
  const inZone = st?.zone.error || zoneRows.length === 0
    ? null
    : zoneRows.every((r) => r.values.includes(targetAddress));

  const mark = {
    close: closed
      ? { state: 'done' as const, word: 'магазин закрыт' }
      : st?.maintenance && !st.maintenance.ok
        ? { state: 'bad' as const, word: 'не удалось' }
        : { state: 'wait' as const, word: 'магазин работает' },
    copy: passLive
      ? { state: 'now' as const, word: 'идёт копия' }
      : pass?.state === 'ok'
        ? {
          state: 'done' as const,
          word: `копия снята ${agoWords(
            Date.now() - new Date(pass.finishedAt ?? pass.startedAt).getTime())}`,
        }
        : pass?.state === 'failed'
          ? { state: 'bad' as const, word: 'копия не удалась' }
          : { state: 'wait' as const, word: 'не начинали' },
    restore: restoreLive
      ? { state: 'now' as const, word: `идёт: ${rest?.progress.phase ?? 'работает'}` }
      : rest?.state === 'ok'
        ? { state: 'done' as const, word: 'развёрнуто' }
        : rest?.state === 'failed'
          ? { state: 'bad' as const, word: 'не удалось' }
          : { state: 'wait' as const, word: 'не начинали' },
    /*
     * Шаг 4 отмечаем по тому, что отвечает мир, а не по тому, что мы просили.
     *
     * Правка зоны может не примениться — и не применилась: имя апекса уехало в
     * путь закодированным, провайдер набора не нашёл, а владелец был уверен,
     * что переключил. Признак — адрес, который отдают резолверы.
     */
    /*
     * Шаг 4 отмечаем по ЗАПИСЯМ В ЗОНЕ, а не по нашему запросу и не по
     * резолверам.
     *
     * Запрос может не примениться — и не применился: имя апекса уехало в путь
     * закодированным, провайдер набора не нашёл. Резолверы отвечают верно, но с
     * запозданием на кеши. Зона отвечает сразу и по существу: правка легла или
     * нет.
     */
    dns: inZone == null
      ? { state: 'wait' as const, word: 'зону не прочитали' }
      : inZone
        ? { state: 'done' as const, word: 'в зоне новый адрес' }
        : { state: 'wait' as const, word: 'в зоне прежний адрес' },
    check: st?.publicCheck
      ? st.publicCheck.ok
        ? { state: 'done' as const, word: 'дошло' }
        : { state: 'bad' as const, word: 'ещё не дошло' }
      : { state: 'wait' as const, word: 'не проверяли' },
  };

  return (
    <section className="panel panel--fill">
      <header className="panel__head">
        <h2 className="panel__title">Ночь переключения</h2>
      </header>

      <div className="panel__scroll wizard">
        <p className="wizard__lead muted">
          Магазин <b>{SITE}</b> переезжает с <code className="mono">{sourceAddress}</code>{' '}
          на <code className="mono">{targetAddress}</code>. Пять шагов, каждый — по
          вашему решению: консоль не переходит к следующему сама. После каждого
          шага уходит письмо.
        </p>

        {failure && <p className="alert alert--error">{failure}</p>}
        {said && <p className="wizard__hint">{said}</p>}

        <Step n={1} title="Закрыть магазин" mark={mark.close}>
          <p className="muted">
            Посетители увидят страницу «переезжаем» с кодом <b>503</b>, а не 200:
            по двумстам поисковые машины решат, что магазин теперь так и выглядит,
            и выбросят страницы из выдачи.{' '}
            <b>Закрывать надо до последней копии</b> — иначе покупатель оформит
            заказ на машине, которую через минуту выключат, и заказ останется в
            базе, которую уже никто не скопирует.
          </p>
          {st?.maintenance && (
            <p className={st.maintenance.ok ? 'wizard__hint' : 'alert alert--error'}>
              {st.maintenance.msg} · {agoWords(Date.now() - new Date(st.maintenance.at).getTime())}
            </p>
          )}
          <div className="wizard__row">
            <button type="button" className="button button--danger"
                    disabled={busy || !source?.online}
                    onClick={() => confirm.ask({
                      title: 'Закрыть боевой магазин?',
                      consequences: [
                        `Покупатели ${SITE} увидят страницу «переезжаем» и не смогут `
                          + 'оформить заказ, пока магазин не откроют обратно.',
                        'Приложение будет остановлено; дерево сайта не изменится ни байтом.',
                        'Проверено на живой машине: настоящая недоступность — 2 секунды.',
                      ],
                      confirmLabel: 'Закрыть',
                      typeToConfirm: SITE,
                      run: () => { void run('Закрытие', 'site.maintenance', {
                        hostId: source?.hostId, siteId: SITE, on: true,
                      }); },
                    })}>
              Закрыть магазин
            </button>
            <button type="button" className="button button--ghost"
                    disabled={busy || !source?.online}
                    onClick={() => confirm.ask({
                      title: 'Открыть магазин обратно?',
                      consequences: [
                        'Заглушка снимается, приложение поднимается, магазин снова '
                          + 'принимает покупателей.',
                        'Это откат первого шага: им же возвращают магазин, если ночь '
                          + 'пойдёт не так.',
                      ],
                      confirmLabel: 'Открыть',
                      run: () => { void run('Открытие', 'site.maintenance', {
                        hostId: source?.hostId, siteId: SITE, on: false,
                      }); },
                    })}>
              Открыть обратно
            </button>
            {!source?.online && (
              <span className="wizard__bad">агент боевой машины не на связи</span>
            )}
          </div>
        </Step>

        <Step n={2} title="Снять последнюю копию" mark={mark.copy}>
          <p className="muted">
            Магазин уже стоит — копия застаёт машину неподвижной. Это единственная
            копия, которую потом нельзя переснять, поэтому снимаем полную, а не
            разностную: скорость здесь дешевле уверенности.
          </p>
          {!closed && (
            <p className="wizard__hint">
              Магазин ещё не закрыт. Копию можно снять и так, но тогда в ней может
              не оказаться последних заказов.
            </p>
          )}
          <button type="button" className="button"
                  disabled={busy || !source?.online}
                  onClick={() => confirm.ask({
                    title: 'Снять последнюю копию?',
                    consequences: [
                      `Полный проход по ${SITE} на боевой машине.`,
                      'Займёт 15–25 минут: это последняя копия перед переездом.',
                    ],
                    confirmLabel: 'Снять копию',
                    run: () => { void run('Копия', 'agents.run-now', {
                      hostId: source?.hostId, siteId: SITE,
                    }); },
                  })}>
            Снять копию сейчас
          </button>
        </Step>

        <Step n={3} title="Развернуть на новой машине" mark={mark.restore}>
          {/*
            Выбор сделан днём, при свете, и записан в рецепт.

            В час ночи выбирать снимок, источники и галочки — это место, где
            ошибаются. Кнопка запускает ровно то, о чём договорились заранее;
            подробный ход и лог по-прежнему на вкладке «Восстановление».
          */}
          {/*
            Предложение приёмника могло не успеть.

            29.08 разворот запустили в 07:23, а список «что не грузить» приёмник
            прислал в 07:36 — на тринадцать минут позже. Тикет ушёл без него, и
            дамп залился целиком: 53 минуты вместо десяти, сорок три лишние
            минуты закрытого магазина. Экран об этом молчал.
          */}
          {st?.recipe?.importDumps && (st.recipe.skipTables?.length ?? 0) === 0 && (
            <p className="wizard__hint">
              Приёмник пока не прислал предложение «что не грузить в базу». Дамп
              зальётся целиком — это около часа вместо десяти минут. Если
              торопиться некуда, стоит дождаться: список приходит с очередным
              отчётом машины.
            </p>
          )}
          {st?.recipe ? (
            <p className="muted">
              Запустится: {st.recipe.why}. Каталог{' '}
              <code className="mono">{st.recipe.into}</code>, база и службы
              поднимаются.
            </p>
          ) : (
            <p className="wizard__hint">
              Рецепт не собран: нет полного снимка либо приёмник не объявил этот
              каталог своим. Разворот тогда — с вкладки «Восстановление».
            </p>
          )}
          <button type="button" className="button button--danger"
                  disabled={busy || !st?.recipe?.snapshotId || !st?.recipe?.targetHostId
                    || restoreLive}
                  onClick={() => confirm.ask({
                    title: 'Развернуть на новой машине?',
                    consequences: [
                      `${st?.recipe?.why ?? ''}.`,
                      'БАЗА НА ПРИЁМНИКЕ БУДЕТ ЗАМЕНЕНА дампом из снимка, '
                        + 'контейнеры подняты заново.',
                      'Боевую машину это не затрагивает: адреса базы и сессий '
                        + 'каркас развёртывания переписывает на здешние.',
                    ],
                    confirmLabel: 'Развернуть',
                    typeToConfirm: SITE,
                    run: () => { void run('Разворот', 'restore.start', {
                      snapshotId: st?.recipe?.snapshotId,
                      targetHostId: st?.recipe?.targetHostId,
                      into: st?.recipe?.into,
                      sources: st?.recipe?.sources,
                      mode: 'resume',
                      prune: false,
                      keep: st?.recipe?.keep,
                      skipTables: st?.recipe?.skipTables,
                      restart: true,
                      importDumps: true,
                    }); },
                  })}>
            {restoreLive ? 'разворот идёт' : 'Развернуть по плану'}
          </button>
          {rest && (
            <p className={restoreLive ? 'wizard__hint' : 'muted'}>
              {restoreLive ? 'Идёт сейчас' : 'Последняя работа'}: {rest.state}
              {rest.progress.note ? ` · ${rest.progress.note}` : ''}
              {' · '}{bytes(rest.progress.bytesWritten ?? rest.progress.bytesUploaded)} на диск
              {rest.progress.bytesNet != null && `, ${bytes(rest.progress.bytesNet)} из сети`}
              {'. '}Подробности и полный лог — на вкладке «Восстановление».
            </p>
          )}
        </Step>

        <Step n={4} title="Переключить домен" mark={mark.dns}>
          <p className="muted">
            Записи <code className="mono">@</code> и <code className="mono">www</code>{' '}
            переписываются с <code className="mono">{sourceAddress}</code> на{' '}
            <code className="mono">{targetAddress}</code>. TTL записей — 60 секунд,
            расхождение займёт около минуты. Прежние значения консоль сохраняет:
            откат не зависит от того, помнит ли их человек в час ночи.
          </p>
          {/*
            Туда и обратно — двумя соседними кнопками.

            Откат домена в эту ночь нужен ровно так же часто, как переключение,
            и искать его на другой вкладке в четыре утра — плохая мысль. Прежние
            значения консоль сохраняет сама, так что «обратно» не зависит от
            того, помнит ли их человек.
          */}
          <div className="wizard__row">
            <button type="button" className="button button--danger"
                    disabled={busy || !zone}
                    onClick={() => confirm.ask({
                      title: 'Переключить домен на новую машину?',
                      consequences: [
                        `Записи @ и www у ${SITE} станут ${targetAddress}.`,
                        'С этой минуты покупатели пойдут на новую машину — по мере '
                          + 'того, как расходятся кеши. TTL 60 секунд.',
                        'Прежние значения консоль сохранит: обратный ход — соседняя кнопка.',
                      ],
                      confirmLabel: 'Переключить',
                      typeToConfirm: SITE,
                      run: () => { void run('Переключение', 'dns.records.switch', {
                        accountId: zone?.accountId, zone: SITE,
                        changes: [
                          { name: '@', type: 'A', values: [targetAddress] },
                          { name: 'www', type: 'A', values: [targetAddress] },
                        ],
                      }); },
                    })}>
              Переключить на новую
            </button>
            <button type="button" className="button button--ghost"
                    disabled={busy || !zone}
                    onClick={() => confirm.ask({
                      title: 'Вернуть домен на старую машину?',
                      consequences: [
                        `Записи @ и www у ${SITE} вернутся на ${sourceAddress}.`,
                        'Это откат переключения. Если старый магазин закрыт заглушкой, '
                          + 'его надо ещё и открыть — кнопкой в первом шаге.',
                      ],
                      confirmLabel: 'Вернуть',
                      run: () => { void run('Возврат домена', 'dns.records.switch', {
                        accountId: zone?.accountId, zone: SITE,
                        changes: [
                          { name: '@', type: 'A', values: [sourceAddress] },
                          { name: 'www', type: 'A', values: [sourceAddress] },
                        ],
                      }); },
                    })}>
              Вернуть на старую
            </button>
          </div>
          {/*
            Что стоит в зоне прямо сейчас.

            Владелец: «мы же по API видим, какие записи стоят!». Видим — и это
            лучший ответ на вопрос «переключилось ли»: резолверы отвечают с
            запозданием на кеши, а зона отвечает сразу.
          */}
          <p className={inZone ? 'wizard__hint' : 'muted'}>
            <b>Сейчас в зоне:</b>{' '}
            {st?.zone.error
              ? <span className="wizard__bad">не прочитали — {st.zone.error}</span>
              : zoneRows.length === 0
                ? 'записей A для @ и www не нашлось'
                : zoneRows.map((r) => (
                  <span key={r.name} className="mono">
                    {r.name} → {r.values.join(', ')}{' '}
                  </span>
                ))}
            {inZone === true && ' — правка легла.'}
            {inZone === false && ' — правка ещё не легла.'}
          </p>
          {/*
            Отказ показываем здесь же, у кнопки.

            Прошлый раз он появился строкой наверху экрана, а владелец смотрел
            на шаг: нажал, не увидел, решил что переключил. Отказ должен быть
            там, где нажимали.
          */}
          {failure && <p className="alert alert--error">{failure}</p>}
          {/*
            Шаг, которого не было ни в тикете, ни в манифесте, ни в списке.

            29.08 после переключения домен перестал отвечать по https вовсе:
            `000`, отказ соединения. Магазин был жив и отвечал по http, а
            выпуск сертификата на приёмнике был выключен НАМЕРЕННО — пока DNS
            смотрел на старую машину, спутник впустую жёг бы попытки.

            Включить его можно только ПОСЛЕ переключения и нельзя заранее.
            Забыть — легко: он ничей. Поэтому он здесь, между записью и
            проверкой, а не в чьей-то памяти (их 296).
          */}
          <p className="alert alert--error">
            <b>Сразу после переключения — включить выпуск сертификата на
            приёмнике.</b> До этого домен не отвечает по https вовсе: не 502, а
            отказ соединения. Нужны оба имени — голое и <code className="mono">www</code>,
            — и пересоздание веб-контейнера. Заранее этот шаг сделать нельзя: пока
            запись смотрела на старую машину, попытки выпуска сгорали впустую.
          </p>
          <p className="muted">
            Все записи зоны и история правок — на вкладке «Имена и домены».
            {zone && <> Зона <b>{zone.name}</b> в консоли есть.</>}
          </p>
        </Step>

        <Step n={5} title="Убедиться, что дошло" mark={mark.check}>
          <p className="muted">
            Приёмник спросит публичные резолверы, на какой адрес указывает домен, и
            прочитает сертификат. <b>Признак переключения — адрес, а не страница</b>:
            старый и новый сайт отдают одну и ту же страницу, в том и смысл
            переезда, и отличать их по содержимому значит принять успех за неудачу.
          </p>
          {/*
            Живая весть важнее итоговой: пока проверка идёт, владельцу нужно
            видеть, что она идёт, а не последний её итог.
          */}
          {st?.publicBeat && !st.publicBeat.done && (
            <p className="wizard__hint">
              {st.publicBeat.msg}
              <span className="muted">
                {' · '}{agoWords(Date.now() - new Date(st.publicBeat.at).getTime())}
              </span>
            </p>
          )}
          {st?.publicCheck && (
            <p className={st.publicCheck.ok ? 'wizard__hint' : 'alert alert--error'}>
              {st.publicCheck.msg} ·{' '}
              {agoWords(Date.now() - new Date(st.publicCheck.at).getTime())}
            </p>
          )}
          <button type="button" className="button"
                  disabled={busy || !target?.online}
                  onClick={() => { void run('Проверка', 'site.public.check', {
                    hostId: target?.hostId, host: SITE,
                    expectAddress: targetAddress, settleMs: 900_000,
                  }); }}>
            Проверить, дошло ли
          </button>
          {!target?.online && (
            <span className="wizard__bad"> агент новой машины не на связи</span>
          )}
        </Step>

        {/* ── 6 ───────────────────────────────────────────────────────── */}
        <section className="wizard__step">
          <h3 className="wizard__title">
            <span className="wizard__num">6</span> Погасить старые машины
          </h3>
          <p className="muted">
            Ради этого переезд и затевался. Цены ниже — из прайса кабинета, а не
            из памяти.
          </p>

          {/*
            Запрет, а не совет.

            29.08 выяснилось, что `pub/static` этого магазина НЕВОССТАНОВИМ:
            исходники темы не собираются с февраля 2022 года, а рабочий
            `styles-l.css` собран в марте 2026 и с тех пор просто лежит. Его нет
            ни в одном снимке — он исключён из копии как «пересчитываемый», а
            пересчёт даёт меньше половины файлов и молча отчитывается об успехе.
            
            Восемнадцать тысяч файлов удалось перенести только потому, что
            старая машина ещё цела. Погаси её сразу после переезда — как принято
            и как собирались, — и оформление магазина было бы потеряно
            безвозвратно.
          */}
          <p className="alert alert--error">
            <b>Старые машины пока удалять нельзя.</b> Оформление магазина
            (<code className="mono">pub/static</code>, 18 447 файлов, 1,29 ГБ)
            существует только на них: исходники темы не собираются с 2022 года,
            а рабочие файлы собраны в марте 2026 и с тех пор просто лежат. Ни в
            одном снимке их нет — они исключены из копии как пересчитываемые, а
            пересчитать их нельзя.
            {' '}Удалять можно <b>только после</b> того, как первый снимок нового
            прода будет содержать статику внутри.
          </p>

          {cost?.why && (
            <p className="wizard__hint">
              Состав машин взят из объявленного списка, а не из кабинета:{' '}
              {cost.why}. Цены при этом настоящие. Заведите ключ проекта с правом
              чтения — и состав будет читаться сам.
            </p>
          )}

          {cost && (
            <>
              <table className="cost">
                <tbody>
                  {cost.going.lines.map((l) => (
                    <tr key={`${l.name}-${l.what}`}
                        className={l.stopsWithServer ? '' : 'cost__stays'}>
                      <td className="mono">{l.name}</td>
                      <td>{l.what}</td>
                      <td className="cost__sum">{l.monthly.toFixed(2)}</td>
                      <td className="muted">
                        {l.stopsWithServer ? '' : 'переживёт удаление машины'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <p className="cost__total">
                <b>{cost.going.monthly.toFixed(2)} {cost.going.currency} в месяц</b>
                <span className="muted">
                  {' '}· {(cost.going.monthly * 12).toFixed(2)} {cost.going.currency} в год
                </span>
              </p>
              {/*
                Гасим не весь кабинет: рядом стоит шлюз, через который в
                интернет ходят все площадки. Сложить его цену в экономию значило
                бы пообещать деньги, которых владелец не увидит, — а по такому
                обещанию потом сверяют счёт.
              */}
              {cost.bill.monthly > cost.going.monthly && (
                <p className="muted">
                  Это только машины переезда ({cost.goingNames.join(', ')}). Весь
                  кабинет обходится в {cost.bill.monthly.toFixed(2)}{' '}
                  {cost.bill.currency}: остальное — то, что остаётся работать.
                </p>
              )}

              {/*
                Удалённые машины — отдельной строкой и до предупреждений.

                Сборщик записи не удаляет: он знает, что машину видел, и не
                знает, что её больше нет. Три недели консоль считала в счёте
                redis и elastic, удалённых 3 сентября, — то есть отвечала «нет»
                на единственный вопрос, ради которого этот экран и заведён.
              */}
              {cost.gone.length > 0 && (
                <p className="wizard__hint">
                  Этих машин в кабинете уже нет, и в счёте выше их тоже нет:{' '}
                  {cost.gone.map((g) => (
                    `${g.name}${g.type ? ` (${g.type})` : ''} — последний раз `
                    + `кабинет показывал её ${new Date(g.lastSeen)
                      .toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' })}`
                  )).join('; ')}. Это и есть та часть экономии, которая уже
                  случилась.
                </p>
              )}

              {/*
                Главное предупреждение этого экрана.

                Машину удалили, а счёт прежний — самая частая история про
                несостоявшуюся экономию. Адрес и том живут сами по себе:
                отвязались от удалённой машины и продолжают стоить. Назвать это
                надо ДО удаления, а не после первого счёта.
              */}
              <p className="report__note">
                С удалением машин перестанет начисляться{' '}
                <b>{cost.going.monthlyWithServer.toFixed(2)} {cost.going.currency}</b>.
                Остальные <b>{cost.going.monthlyLeftover.toFixed(2)}</b> —
                адреса IPv4 и тома — <b>продолжат тикать</b>: они живут сами по
                себе и отвязываются от удалённой машины, а не исчезают вместе с
                ней. Их надо удалять отдельно, и именно на них экономия обычно и
                не случается.
              </p>

              <h4 className="report__head">Когда эти деньги уйдут из счёта</h4>
              <ol className="report__not">
                <li>
                  <b>В час удаления.</b> Провайдер считает по часам — начисление
                  прекращается сразу, не дожидаясь конца месяца.
                </li>
                <li>
                  <b>Счёт за {cost.when.partialInvoiceFor}</b> придёт неполным: в
                  нём будут часы до удаления. Сейчас в этом месяце уже набежало{' '}
                  {count(cost.when.hoursCharged)} ч.
                </li>
                <li>
                  <b>Первый чистый счёт — за {cost.when.firstCleanInvoiceFor}.</b>{' '}
                  Только его и надо сравнивать с прежними.
                </li>
              </ol>
              <p className="muted">
                Отсюда неочевидное: сравнивать надо не следующий счёт с прошлым, а{' '}
                <b>после-следующий</b>. Иначе экономия выглядит вдвое меньшей, и
                находится тот, кто решит, что переезд не окупился.
              </p>
              <p className="muted">
                Гасить машины <b>не сейчас</b>. Пока новая работает считанные дни,
                старая — единственный способ вернуться назад одним переключением
                записи. Разумный срок — неделя спокойной работы: за неё видно и
                ночные копии, и поведение под нагрузкой буднего дня.
              </p>
            </>
          )}
        </section>

        {/* ── 7 ───────────────────────────────────────────────────────── */}
        <Step n={7} title="Завести копирование на новом месте"
              mark={first?.state === 'running'
                ? { state: 'now', word: `идёт первая копия · ${count(first.files)} файлов` }
                : first?.state === 'ok'
                  ? { state: 'done', word: 'первая копия снята' }
                  : first?.state === 'failed'
                    ? { state: 'bad', word: 'копия не удалась' }
                    : ready?.enabled
                      ? { state: 'done', word: 'копирование включено' }
                : ready?.adopted
                  ? { state: 'now', word: 'сайт заведён, копирование выключено' }
                  : ready?.declared
                    ? { state: 'now', word: 'машина объявила сайт — можно заводить' }
                      : { state: 'wait', word: 'ждём манифест приёмника' }}>
          <p className="muted">
            Переезд не кончается переключением домена. Пока новая машина не
            копируется, магазин живёт <b>без единой копии</b> — и именно в те
            дни, когда всё новое и ломается охотнее всего.
          </p>
          {/*
            Три состояния, и путать их нельзя.

            Машина может ОБЪЯВИТЬ сайт в своём манифесте, консоль — ЗАВЕСТИ его
            у себя, и только заведённый можно включить. Владелец жмёт «включить
            копирование» и получает «сайта на этой машине нет» — хотя машина
            про него знает.
          */}
          {ready?.declared ? (
            <p className="wizard__hint">
              Приёмник объявил сайт: источники{' '}
              <b>{ready.sources.join(', ') || '—'}</b>.
              {!ready.adopted && ' Осталось завести его в консоли.'}
            </p>
          ) : (
            <p className="muted">
              Приёмник ещё не объявил сайт в своём манифесте: какие каталоги
              считать источниками, где база, что исключать. Запрос команде агента
              отправлен.
            </p>
          )}
          {ready?.declared && !ready.adopted && (
            <button type="button" className="button"
                    disabled={busy}
                    onClick={() => { void run('Заведение сайта', 'agent.site.adopt', {
                      hostId: ready.hostId, siteId: SITE,
                    }); }}>
              Завести сайт в консоли
            </button>
          )}
          {/*
            Идущая копия видна здесь, а не только в «Агентах».

            Владелец нажал «снять первую копию», копия пошла — и на экране не
            изменилось ничего: шаг смотрел не на ту машину, а письмо приходит
            только по окончании. Нажатие без отклика читается как неработающая
            кнопка.
          */}
          {first && (
            <p className={first.state === 'running' ? 'wizard__hint' : 'muted'}>
              {first.state === 'running' ? 'Идёт первая копия' : 'Последняя копия здесь'}:{' '}
              {count(first.files)} файлов, прочитано {bytes(first.bytes)}, в хранилище{' '}
              {bytes(first.uploaded)}
              {first.phase ? ` · ${first.phase}` : ''}
              {first.state !== 'running' && ` · ${first.state}`}
              {'. '}Письмо придёт по окончании.
            </p>
          )}

          <div className="wizard__row">
            <button type="button" className="button"
                    disabled={busy || !target?.online || !ready?.adopted || ready.enabled}
                    onClick={() => confirm.ask({
                      title: 'Включить копирование на новой машине?',
                      consequences: [
                        `${SITE} на ${target?.label ?? 'новой машине'} начнёт копироваться `
                          + 'по расписанию.',
                        'Первую копию стоит снять сразу же, не дожидаясь ночи.',
                      ],
                      confirmLabel: 'Включить',
                      run: () => { void run('Копирование', 'site.copying', {
                        hostId: target?.hostId, siteId: SITE, enabled: true,
                      }); },
                    })}>
              Включить копирование на новой
            </button>
            <button type="button" className="button"
                    disabled={busy || !target?.online || !ready?.enabled}
                    onClick={() => { void run('Первая копия', 'agents.run-now', {
                      hostId: target?.hostId, siteId: SITE,
                    }); }}>
              Снять первую копию
            </button>
          </div>

          <p className="muted">
            {/*
              Выключить старую — не уборка, а тишина в журнале.

              Сайт, оставшийся включённым на выключаемой машине, каждую ночь
              даёт неудачу. Через неделю на неудачи перестают смотреть — вместе
              с настоящими.
            */}
            И обратное: на старой машине копирование надо <b>погасить</b>, иначе
            каждую ночь будет неудача, а через неделю на неудачи перестанут
            смотреть — вместе с настоящими. Ведро старой машины при этом остаётся:
            в нём лежат копии, которые ещё пригодятся, — но новых туда не будет.
          </p>
          <button type="button" className="button button--ghost"
                  disabled={busy || !source?.online}
                  onClick={() => confirm.ask({
                    title: 'Выключить копирование на старой машине?',
                    consequences: [
                      `${SITE} на ${source?.label ?? 'старой машине'} перестанет `
                        + 'копироваться по ночам.',
                      'Уже снятые копии остаются на месте и доступны для восстановления.',
                      'Включить обратно можно этой же кнопкой.',
                    ],
                    confirmLabel: 'Выключить',
                    run: () => { void run('Копирование старой', 'site.copying', {
                      hostId: source?.hostId, siteId: SITE, enabled: false,
                    }); },
                  })}>
            Выключить копирование на старой
          </button>
        </Step>

        {/*
          Журнал — внизу, после шагов: он отвечает на «что было», а шаги на
          «что сейчас». Сначала место, потом история.

          Перед журналом — итог: у закрытого мероприятия он главнее трёхсот
          строк ленты, а у открытого напоминает, что мероприятие ещё открыто.
        */}
        <CampaignFinish id={CAMPAIGN} />
        <CampaignLog id={CAMPAIGN} />

        <section className="wizard__step">
          <h3 className="wizard__title">Если пойдёт не так</h3>
          <p className="muted">
            До четвёртого шага откат ничего не стоит: боевая машина цела, файлы и
            база не тронуты, изменена только конфигурация nginx в контейнере.
            Открыть магазин — кнопкой «Открыть обратно» выше. После переключения
            домена откат — вернуть записи на <code className="mono">{sourceAddress}</code>{' '}
            на вкладке «Домены» и открыть магазин.
          </p>
        </section>
      </div>

      <Confirm
        open={Boolean(confirm.pending)}
        title={confirm.pending?.title ?? ''}
        consequences={confirm.pending?.consequences ?? []}
        confirmLabel={confirm.pending?.confirmLabel ?? 'Подтвердить'}
        {...(confirm.pending?.typeToConfirm
          ? { typeToConfirm: confirm.pending.typeToConfirm } : {})}
        busy={busy}
        onConfirm={() => { const go = confirm.pending?.run; confirm.close(); void go?.(); }}
        onCancel={confirm.close}
      />
    </section>
  );
}
